import { useState } from 'react';
import { Permission, decodePermissions, has } from '@callzone/shared';
import type { Member } from '@callzone/shared';
import { api } from '../lib/api';
import { voice } from '../lib/voice';
import {
  displayName,
  memberAccent,
  memberColor,
  originalName,
  useActiveGuild,
  useAliases,
  useStore,
} from '../store';
import { Avatar } from './Avatar';
import { CloseIcon, PullIcon, VolumeIcon, VolumeOffIcon } from './icons';

export function MemberList({ onClose }: { onClose?: () => void }) {
  const guild = useActiveGuild();
  const presences = useStore((s) => s.presences);
  const [selected, setSelected] = useState<string | null>(null);
  useAliases();

  if (!guild) return null;

  // Cargos com "hoist" ganham secao propria, como no Discord.
  const hoisted = guild.roles.filter((r) => r.hoist).sort((a, b) => b.position - a.position);
  const used = new Set<string>();

  const groups: Array<{ title: string; color?: string | null; members: Member[] }> = [];

  for (const role of hoisted) {
    const members = guild.members.filter(
      (m) =>
        !used.has(m.userId) &&
        m.roleIds.includes(role.id) &&
        (presences[m.userId] ?? 'offline') !== 'offline',
    );
    for (const m of members) used.add(m.userId);
    if (members.length) groups.push({ title: role.name, color: role.color, members });
  }

  const online = guild.members.filter(
    (m) => !used.has(m.userId) && (presences[m.userId] ?? 'offline') !== 'offline',
  );
  if (online.length) groups.push({ title: `Online — ${online.length}`, members: online });

  const offline = guild.members.filter((m) => (presences[m.userId] ?? 'offline') === 'offline');
  if (offline.length) groups.push({ title: `Offline — ${offline.length}`, members: offline });

  return (
    <>
      <div className="member-list">
        {onClose && (
          <div className="drawer-head">
            <span>Membros</span>
            <button className="icon-btn" onClick={onClose} title="Fechar" aria-label="Fechar">
              <CloseIcon />
            </button>
          </div>
        )}
        {groups.map((group) => (
          <div key={group.title}>
            <div className="member-group-title" style={group.color ? { color: group.color } : undefined}>
              {group.title}
            </div>
            {group.members.map((member) => {
              const status = presences[member.userId] ?? 'offline';
              const color = memberColor(guild, member.userId);
              return (
                <button
                  key={member.userId}
                  className={`member-row ${status === 'offline' ? 'offline' : ''}`}
                  onClick={() => setSelected(member.userId)}
                >
                  <Avatar
                    name={displayName(guild, member.userId)}
                    color={memberAccent(guild, member.userId)}
                    size={30}
                    presence={status}
                    userId={member.userId}
                    avatarVersion={member.user.avatarVersion}
                  />
                  <span className="name" style={color ? { color } : undefined}>
                    {displayName(guild, member.userId)}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {selected && <MemberPanel userId={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function MemberPanel({ userId, onClose }: { userId: string; onClose: () => void }) {
  const guild = useActiveGuild();
  const me = useStore((s) => s.me);
  const voiceStates = useStore((s) => s.voiceStates);
  const toast = useStore((s) => s.toast);
  const aliases = useAliases();
  const volume = useStore((s) => s.volumes[userId] ?? 1);
  const setAliasLocal = useStore((s) => s.setAlias);
  const alias = aliases[userId] ?? null;
  const [aliasDraft, setAliasDraft] = useState(alias ?? '');
  const [busy, setBusy] = useState(false);

  if (!guild) return null;
  const member = guild.members.find((m) => m.userId === userId);
  if (!member) return null;

  /** Campo vazio apaga o apelido e volta ao nome original. */
  async function salvarApelido() {
    const valor = aliasDraft.trim() || null;
    setBusy(true);
    try {
      await api.setAlias(userId, valor);
      setAliasLocal(userId, valor);
      toast(valor ? `Agora você vê essa pessoa como ${valor}.` : 'Apelido removido.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nao consegui salvar o apelido.', 'error');
    } finally {
      setBusy(false);
    }
  }

  const permissions = decodePermissions(guild.permissions);
  const canManageRoles = has(permissions, Permission.MANAGE_ROLES);
  const canKick = has(permissions, Permission.KICK_MEMBERS);
  const canMute = has(permissions, Permission.MUTE_MEMBERS);
  const canMove = has(permissions, Permission.MOVE_MEMBERS);
  const isSelf = userId === me?.id;
  const isOwner = guild.guild.ownerId === userId;
  const voiceState = voiceStates[userId];
  /** Canal onde EU estou: destino do "puxar". Sem ele, nao ha para onde puxar. */
  const myVoiceChannelId = me ? (voiceStates[me.id]?.channelId ?? null) : null;

  async function guarded(action: () => Promise<unknown>, successMessage?: string) {
    setBusy(true);
    try {
      await action();
      if (successMessage) toast(successMessage);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Acao recusada.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              name={displayName(guild, userId)}
              color={memberAccent(guild, userId)}
              size={44}
              userId={userId}
              avatarVersion={member.user.avatarVersion}
            />
            <div>
              <h2>{displayName(guild, userId)}</h2>
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                {member.user.fullName ? `${member.user.fullName} · ` : ''}
                {isOwner ? 'Dona(o) da guild' : `entrou em ${new Date(member.joinedAt).toLocaleDateString('pt-BR')}`}
              </div>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Fechar">
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <label>Cargos</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {guild.roles
                .filter((r) => !r.isDefault)
                .map((role) => {
                  const active = member.roleIds.includes(role.id);
                  return (
                    <button
                      key={role.id}
                      className="role-chip"
                      style={{
                        opacity: active ? 1 : 0.42,
                        cursor: canManageRoles ? 'pointer' : 'default',
                      }}
                      disabled={!canManageRoles || busy}
                      onClick={() =>
                        void guarded(() =>
                          active
                            ? api.removeRole(guild.guild.id, userId, role.id)
                            : api.addRole(guild.guild.id, userId, role.id),
                        )
                      }
                    >
                      <span className="dot" style={{ background: role.color ?? 'var(--text-dim)' }} />
                      {role.name}
                    </button>
                  );
                })}
            </div>
            {canManageRoles && (
              <div className="hint">Clique num cargo para conceder ou remover.</div>
            )}
          </div>

          {!isSelf && (
            <div className="field">
              <label>Apelido só para você</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={aliasDraft}
                  onChange={(e) => setAliasDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void salvarApelido()}
                  placeholder={originalName(guild, userId)}
                  maxLength={32}
                />
                <button
                  className="btn small"
                  disabled={busy || aliasDraft.trim() === (alias ?? '')}
                  onClick={() => void salvarApelido()}
                >
                  Salvar
                </button>
              </div>
              <div className="hint">
                {alias
                  ? `Você vê "${alias}"; para todo mundo continua ${originalName(guild, userId)}. Apague o campo para desfazer.`
                  : 'Só você vê esse nome. A pessoa não fica sabendo.'}
              </div>
            </div>
          )}

          {voiceState?.channelId && !isSelf && (
            <div className="field">
              <label>Volume desta pessoa</label>
              <div className="volume-row">
                <VolumeOffIcon />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(volume * 100)}
                  onChange={(e) => voice.setUserVolume(userId, Number(e.target.value) / 100)}
                />
                <VolumeIcon />
                <span className="volume-value">{Math.round(volume * 100)}%</span>
              </div>
              <div className="hint">
                Vale só para você e fica guardado neste navegador. Os outros continuam ouvindo
                normal.
              </div>
            </div>
          )}

          {voiceState?.channelId && !isSelf && (canMute || canMove) && (
            <div className="field">
              <label>Moderacao de voz</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canMove && myVoiceChannelId && voiceState.channelId !== myVoiceChannelId && (
                  <button
                    className="btn small"
                    disabled={busy}
                    onClick={() =>
                      void guarded(
                        () =>
                          api.moderateVoice(guild!.guild.id, userId, {
                            moveToChannelId: myVoiceChannelId,
                          }),
                        'Membro puxado para o seu canal.',
                      )
                    }
                  >
                    <PullIcon style={{ width: 15, height: 15 }} />
                    Puxar para o meu canal
                  </button>
                )}
                {canMute && (
                  <button
                    className="btn ghost small"
                    disabled={busy}
                    onClick={() =>
                      void guarded(
                        () =>
                          api.moderateVoice(guild.guild.id, userId, {
                            serverMute: !voiceState.serverMute,
                          }),
                        voiceState.serverMute ? 'Microfone liberado.' : 'Membro silenciado.',
                      )
                    }
                  >
                    {voiceState.serverMute ? 'Liberar microfone' : 'Silenciar no servidor'}
                  </button>
                )}
                {canMove && (
                  <button
                    className="btn danger small"
                    disabled={busy}
                    onClick={() =>
                      void guarded(
                        () => api.moderateVoice(guild.guild.id, userId, { disconnect: true }),
                        'Membro desconectado da call.',
                      )
                    }
                  >
                    Desconectar da call
                  </button>
                )}
              </div>
            </div>
          )}

          {canKick && !isSelf && !isOwner && (
            <div className="field">
              <label>Zona de perigo</label>
              <button
                className="btn danger small"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(`Remover ${displayName(guild, userId)} da guild?`)) return;
                  void guarded(async () => {
                    await api.kickMember(guild.guild.id, userId);
                    onClose();
                  }, 'Membro removido.');
                }}
              >
                Expulsar da guild
              </button>
              <div className="hint">
                A pessoa perde o acesso na hora e so volta com um novo convite.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
